import { InputLabel, MenuItem } from '@mui/material';
import { AxiosResponse } from 'axios';
import { memo } from 'react';
import { __ANPR_PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseListPayloadQuarkus } from 'shell/models/http-response';
import { ItemVM } from 'shell/view-models/item';
import { PmiSelect } from '../pmi-select';
import { PmiFormControl } from '../pmi-select/pmi-form-control';

interface ParenthoodRelationshipListComponentProps {
  disabled?: boolean;
  isHeadParenthood?: boolean;
  isHeadMarriage?: boolean;
  value: string;
  onChange: (parenthoodRelationship?: string) => void;
  error?: boolean;
  label?: string;
  id?: string;
  required?: boolean;
  isPractice?: boolean;
  filterParenthood?: (items: ItemVM, isHeadMarriage: boolean, isHeadParenthood: boolean) => boolean;
}

type Key = {
  codice: string;
};

interface ParenthoodRelationship {
  key: Key;
  descrizione: string;
}

const ParenthoodRelationshipListComponent = ({
  disabled,
  onChange,
  value,
  error,
  required,
  isHeadParenthood = false,
  isHeadMarriage = false,
  label = 'Relazione parentela',
  id = 'parenthoodRelationship',
  isPractice,
  filterParenthood,
}: ParenthoodRelationshipListComponentProps): React.ReactElement => {
  const [parenthoodRelationship] = useHttpFetch<
    HttpResponseListPayloadQuarkus<ParenthoodRelationship>,
    Array<ItemVM>
  >(`${__ANPR_PERSON_NEW_API_URL__}/relazione-parentela`, {
    onBootstrap: true,
    options: {
      params: {
        sort: 'descrizione:ASC',
        filters: isPractice ? 'pratiche:pratiche' : undefined,
      },
    },
    transformer: (
      response: AxiosResponse<HttpResponseListPayloadQuarkus<ParenthoodRelationship>>
    ) => {
      const { data } = response;

      return data?.payload.map((item) => ({
        id: item.key.codice?.toString(),
        value: item.descrizione,
      })) as Array<ItemVM>;
    },
  });

  const list = ((parenthoodRelationship.data as Array<ItemVM>) || []).filter((parenthood) => {
    if (filterParenthood) {
      return filterParenthood(parenthood, isHeadMarriage, isHeadParenthood);
    }

    if (isHeadParenthood && isHeadMarriage) {
      return parenthood.id?.toString() !== '1' && parenthood.id?.toString() !== '2';
    }

    if (isHeadParenthood) {
      return parenthood.id?.toString() !== '1';
    }

    if (isHeadMarriage) {
      return parenthood.id?.toString() !== '2';
    }

    return true;
  });

  const idTag = `${id}-${label}`;

  return (
    <PmiFormControl fullWidth>
      <InputLabel error={error} disabled={disabled} id={idTag}>
        {required ? `${label} *` : label || undefined}
      </InputLabel>
      <PmiSelect
        labelId={idTag}
        id={id}
        name={id}
        value={value}
        label={required ? `${label} *` : label || undefined}
        onChange={(event) => {
          const selectedValue = event.target.value;
          onChange(selectedValue);
        }}
        disabled={disabled}
        error={error}
      >
        <MenuItem value="">
          <em>Nessuno</em>
        </MenuItem>
        {list.map((item: ItemVM) => (
          <MenuItem key={item.id} value={item.id}>
            {item.value}
          </MenuItem>
        ))}
      </PmiSelect>
    </PmiFormControl>
  );
};

ParenthoodRelationshipListComponent.displayName = 'Parenthood Relationship List component';

const ParenthoodRelationshipList = memo(ParenthoodRelationshipListComponent);

export default ParenthoodRelationshipList as typeof ParenthoodRelationshipListComponent;
